import * as Yup from "yup";
import { isTelehealthPlaceOfService } from "./use-service-location-billing";
interface ProcedureCode {
  [key: string]: string;
}

interface Modifiers {
  [key: string]: string;
}

export interface GetSuperBillConst {
  uuid: string;
  procedureCode: ProcedureCode;
  diagnosisFirstPointers: string;
  diagnosisSecondPointers: string;
  diagnosisThirdPointers: string;
  diagnosisFourthPointers: string;
  medicalCodeModifiers: string;
  modifiers: Modifiers;
  quantity: number;
  total: number;
  charges: number;
  discount: number;
  discountType: "PERCENTAGE" | "AMOUNT";
  active: boolean;
  archive: boolean;
}

export const SUPERBILLPLACEOFSERVICEMAPPER = {
  PHARMACY: "01 - Pharmacy",
  TELEHEALTH_OUTSIDE_PATIENT_HOME:
    "02 - Telehealth Provided Other than in Patient's Home",
  SCHOOL: "03 - School",
  HOMELESS_SHELTER: "04 - Homeless Shelter",
  INDIAN_HEALTH_SERVICE_FREE_STANDING_FACILITY:
    "05 - Indian Health Service Free-standing Facility",
  INDIAN_HEALTH_SERVICE_PROVIDER_BASED_FACILITY:
    "06 - Indian Health Service Provider-based Facility",
  TRIBAL_638_FREE_STANDING_FACILITY: "07 - Tribal 638 Free-standing Facility",
  TRIBAL_638_PROVIDER_BASED_FACILITY: "08 - Tribal 638 Provider-based Facility",
  PRISON_CORRECTIONAL_FACILITY: "09 - Prison/Correctional Facility",
  TELEHEALTH_INSIDE_PATIENT_HOME: "10 - Telehealth Provided in Patient's Home",
  OFFICE: "11 - Office",
  HOME: "12 - Home",
  ASSISTED_LIVING_FACILITY: "13 - Assisted Living Facility",
  GROUP_HOME: "14 - Group Home",
  MOBILE_UNIT: "15 - Mobile Unit",
  TEMPORARY_LODGING: "16 - Temporary Lodging",
  WALK_IN_RETAIL_HEALTH_CLINIC: "17 - Walk-in Retail Health Clinic",
  PLACE_OF_EMPLOYMENT_WORKSITE: "18 - Place of Employment-Worksite",
  OFF_CAMPUS_OUTPATIENT_HOSPITAL: "19 - Off Campus-Outpatient Hospital",
  URGENT_CARE_FACILITY: "20 - Urgent Care Facility",
  INPATIENT_HOSPITAL: "21 - Inpatient Hospital",
  OUTPATIENT_HOSPITAL: "22 - Outpatient Hospital",
  EMERGENCY_ROOM_HOSPITAL: "23 - Emergency Room – Hospital",
  AMBULATORY_SURGICAL_CENTER: "24 - Ambulatory Surgical Center",
  BIRTHING_CENTER: "25 - Birthing Center",
  MILITARY_TREATMENT_FACILITY: "26 - Military Treatment Facility",
  SKILLED_NURSING_FACILITY: "31 - Skilled Nursing Facility",
  NURSING_FACILITY: "32 - Nursing Facility",
  CUSTODIAL_CARE_FACILITY: "33 - Custodial Care Facility",
  HOSPICE: "34 - Hospice",
  AMBULANCE_LAND: "41 - Ambulance - Land",
  AMBULANCE_AIR_OR_WATER: "42 - Ambulance – Air or Water",
  INDEPENDENT_CLINIC: "49 - Independent Clinic",
  FEDERALLY_QUALIFIED_HEALTH_CENTER: "50 - Federally Qualified Health Center",
  INPATIENT_PSYCHIATRIC_FACILITY: "51 - Inpatient Psychiatric Facility",
  PSYCHIATRIC_FACILITY_PARTIAL_HOSPITALIZATION:
    "52 - Psychiatric Facility-Partial Hospitalization",
  COMMUNITY_MENTAL_HEALTH_CENTER: "53 - Community Mental Health Center",
  INTERMEDIATE_CARE_FACILITY_INTELLECTUAL_DISABILITIES:
    "54 - Intermediate Care Facility/ Individuals with Intellectual Disabilities",
  RESIDENTIAL_SUBSTANCE_ABUSE_TREATMENT_FACILITY:
    "55 - Residential Substance Abuse Treatment Facility",
  PSYCHIATRIC_RESIDENTIAL_TREATMENT_CENTER:
    "56 - Psychiatric Residential Treatment Center",
  NON_RESIDENTIAL_SUBSTANCE_ABUSE_TREATMENT_FACILITY:
    "57 - Non-residential Substance Abuse Treatment Facility",
  NON_RESIDENTIAL_OPIOID_TREATMENT_FACILITY:
    "58 - Non-residential Opioid Treatment Facility",
  MASS_IMMUNIZATION_CENTER: "60 - Mass Immunization Center",
  COMPREHENSIVE_INPATIENT_REHABILITATION_FACILITY:
    "61 - Comprehensive Inpatient Rehabilitation Facility",
  COMPREHENSIVE_OUTPATIENT_REHABILITATION_FACILITY:
    "62 - Comprehensive Outpatient Rehabilitation Facility",
  END_STAGE_RENAL_DISEASE_TREATMENT_FACILITY:
    "65 - End-Stage Renal Disease Treatment Facility",
  PUBLIC_HEALTH_CLINIC: "71 - Public Health Clinic",
  RURAL_HEALTH_CLINIC: "72 - Rural Health Clinic",
  INDEPENDENT_LABORATORY: "81 - Independent Laboratory",
  OTHER_PLACE_OF_SERVICE: "99 - Other Place of Service",
} as Record<string, string>;

export const superBillValidationSchema = Yup.object({
  // ECRH-5370: Service Location is optional for Telehealth encounters.
  locationId: Yup.object()
    .label("Location")
    .when("placeOfService", {
      is: (pos: string) => !isTelehealthPlaceOfService(pos),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
  placeOfService: Yup.string().required().label("Place of Service"),
  dateOfService: Yup.string().required().label("Date of Service"),
  serviceState: Yup.string().required().label("Service State"),
  renderingproviderId: Yup.object().required().label("Rendering Provider"),
  // diagnosisCodes: Yup.array()
  //   .of(Yup.object().required().label("Diagnosis Code"))
  //   .min(1, "At least one ICD Code is required")
  //   .required()
  //   .label("ICD Codes"),
  // procedureCode: Yup.object().required().label("CPT Codes"),

  // priorAuthorization: Yup.string().required().label("Prior Authorization"),
  // billingProviderId: Yup.string().required().label("Billing Provider"),
  // referringProviderId: Yup.string().required().label("Referring Provider"),
  // orderingProviderId: Yup.string().required().label("Ordering Provider"),
});

// Per-row validation for CPT/procedure code discounts. Prevents a discount that
// exceeds the charge amount (which would produce a negative Total Balance) and a
// percentage discount over 100. Mirrors the backend `validateDiscount` guard.
export const procedureCodeValidationSchema = Yup.array().of(
  Yup.object({
    discount: Yup.number()
      .transform((value, original) =>
        original === "" || original === null || original === undefined
          ? undefined
          : value
      )
      .min(0, "Discount cannot be less than 0.")
      .test("discount-within-limit", "Invalid discount", function (value) {
        if (value == null) return true;
        const { discountType, charges } = this.parent as {
          discountType?: "PERCENTAGE" | "AMOUNT";
          charges?: number | string;
        };
        if (discountType === "PERCENTAGE") {
          return (
            value <= 100 ||
            this.createError({
              message: "Discount percentage cannot exceed 100.",
            })
          );
        }
        if (discountType === "AMOUNT") {
          const chargeAmount = Number(charges) || 0;
          return (
            value <= chargeAmount ||
            this.createError({
              message: "Discount amount cannot exceed the charge amount.",
            })
          );
        }
        return true;
      }),
  })
);

